import { createBlankBoardSession } from '../canned/blankBoard'
import { createYuccaDigInSession } from '../canned/yuccaDigIn'
import {
  createFireEvacuationSession,
  createResidentialGasLeakSession,
} from '../canned/yuccaSlices'
import { objectAnchorLngLat } from '../geo'
import {
  ensurePosterPlateMapRegistered,
  MAP_POSTER_PLATE_REF,
  MAP_POSTER_PLATE_STYLE_URL,
  MAP_POSTER_STYLE_ID,
} from '../maps'
import { resolveSession } from '../resolveSession'
import type { ScenarioObject, ScenarioSession } from '../types'

export {
  ensurePosterPlateMapRegistered,
  MAP_POSTER_PLATE_REF,
  MAP_POSTER_PLATE_STYLE_URL,
  MAP_POSTER_STYLE_ID,
}

export type MapPosterCamera = { lng: number; lat: number; zoom: number }

export type MapPosterFit = {
  /** Inset from frame edges when fitting objects (px). Lower = tighter crop. */
  paddingPx?: number
  /** Cap zoom so labels/icons don’t pixelate (Mapbox GL re-renders either way). */
  maxZoom?: number
  /** Floor zoom so a single pin doesn’t fill the whole frame. */
  minZoom?: number
  /** Expand a tiny cluster to at least this span (meters) before fitting. */
  minSpanM?: number
  /** Drop these object types from the fit (still drawn on the poster). */
  excludeTypes?: readonly string[]
  /** If set, only these object ids drive the camera. */
  includeIds?: readonly string[]
}

export type MapPosterShot = {
  /** Stable id — also the PNG basename under public/scenario-posters/. */
  id: string
  width: number
  height: number
  /** Build the board to render (objects + story clock). */
  createSession: () => ScenarioSession
  /**
   * Story playhead for resolveSession. Defaults to storyEndTime (or startsAt)
   * so chapter objects that appear mid-slice are on screen.
   */
  playheadIso?: (session: ScenarioSession) => string
  /**
   * Override camera entirely. When omitted, frames visible objects tightly
   * (see fit) and falls back to the session camera beat if there are none.
   */
  camera?: (session: ScenarioSession, playheadIso: string) => MapPosterCamera
  /** Object-fit framing (ignored when `camera` is set). */
  fit?: MapPosterFit
}

const POSTER_SIZE = { width: 1280, height: 720 } as const

/** Default object framing — features should occupy a solid share of the 16:9 frame. */
const DEFAULT_FIT = {
  paddingPx: 56,
  maxZoom: 17.6,
  minZoom: 15,
  minSpanM: 140,
} satisfies Required<Pick<MapPosterFit, 'paddingPx' | 'maxZoom' | 'minZoom' | 'minSpanM'>>

/**
 * Zone radii are huge vs icons; using the full radius keeps posters zoomed out.
 * Cap how much a circle contributes to the fit so pins/labels can fill the frame.
 */
const FIT_RADIUS_CAP_M = 48

function defaultPlayhead(session: ScenarioSession): string {
  return session.storyEndTime ?? session.storyStartTime
}

function cameraAtPlayhead(session: ScenarioSession, playheadIso: string): MapPosterCamera {
  const at = Date.parse(playheadIso)
  const beats = [...(session.cameraBeats ?? [])].sort(
    (a, b) => Date.parse(a.at) - Date.parse(b.at),
  )
  let cam = session.camera
  if (Number.isFinite(at)) {
    for (const beat of beats) {
      const t = Date.parse(beat.at)
      if (!Number.isFinite(t) || t > at) break
      cam = { lng: beat.lng, lat: beat.lat, zoom: beat.zoom }
    }
  }
  return cam
}

function withPosterPlate(session: ScenarioSession): ScenarioSession {
  return { ...session, activeMapId: MAP_POSTER_STYLE_ID }
}

function metersToLatDeg(m: number): number {
  return m / 111_320
}

function metersToLngDeg(m: number, lat: number): number {
  const cos = Math.cos((lat * Math.PI) / 180)
  return m / (111_320 * Math.max(0.2, Math.abs(cos)))
}

function mercatorY(lat: number): number {
  const rad = (lat * Math.PI) / 180
  return (1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2
}

function objectRadiusM(obj: ScenarioObject): number {
  const raw = Number(obj.params?.radiusM)
  return Number.isFinite(raw) && raw > 0 ? raw : 0
}

/**
 * Frame anchors (+ zone radii) so sample features fill the poster, not a wide
 * neighborhood overview. Pure math — no Mapbox instance required.
 */
export function cameraFittingObjects(
  session: ScenarioSession,
  playheadIso: string,
  size: { width: number; height: number },
  fit: MapPosterFit = {},
): MapPosterCamera | null {
  const opts = { ...DEFAULT_FIT, ...fit }
  const excludeTypes = new Set(opts.excludeTypes ?? ['label'])
  const includeIds = opts.includeIds?.length ? new Set(opts.includeIds) : null
  const board = resolveSession(session, playheadIso, 'authoring', 'author')
  type Pt = { lng: number; lat: number; r: number }
  const pts: Pt[] = []
  for (const obj of board.objects) {
    if (includeIds && !includeIds.has(obj.id)) continue
    if (obj.type && excludeTypes.has(obj.type)) continue
    const anchor = objectAnchorLngLat(obj)
    if (!anchor) continue
    pts.push({
      lng: anchor.lng,
      lat: anchor.lat,
      r: Math.min(FIT_RADIUS_CAP_M, objectRadiusM(obj)),
    })
  }
  if (!pts.length) return null

  let west = Infinity
  let east = -Infinity
  let south = Infinity
  let north = -Infinity
  for (const p of pts) {
    const dLat = metersToLatDeg(p.r)
    const dLng = metersToLngDeg(p.r, p.lat)
    west = Math.min(west, p.lng - dLng)
    east = Math.max(east, p.lng + dLng)
    south = Math.min(south, p.lat - dLat)
    north = Math.max(north, p.lat + dLat)
  }

  const midLat = (south + north) / 2
  const midLng = (west + east) / 2
  const spanM = Math.max(
    (east - west) * 111_320 * Math.cos((midLat * Math.PI) / 180),
    (north - south) * 111_320,
  )
  if (spanM < opts.minSpanM) {
    const half = opts.minSpanM / 2
    west = midLng - metersToLngDeg(half, midLat)
    east = midLng + metersToLngDeg(half, midLat)
    south = midLat - metersToLatDeg(half)
    north = midLat + metersToLatDeg(half)
  }

  const pad = Math.max(24, opts.paddingPx)
  const usableW = Math.max(64, size.width - pad * 2)
  const usableH = Math.max(64, size.height - pad * 2)
  // Mapbox GL zoom uses 512px world tiles; nudge slightly so fit feels “poster tight”.
  const WORLD = 512
  const xSpan = Math.max(1e-9, Math.abs((east - west) / 360))
  const ySpan = Math.max(1e-9, Math.abs(mercatorY(south) - mercatorY(north)))
  const zoomX = Math.log2(usableW / (WORLD * xSpan))
  const zoomY = Math.log2(usableH / (WORLD * ySpan))
  const zoom = Math.min(
    opts.maxZoom,
    Math.max(opts.minZoom, Math.min(zoomX, zoomY) + 0.35),
  )

  return { lng: midLng, lat: midLat, zoom }
}

/**
 * Named poster shots. Regen with `yarn workspace @buxus/web gen:map-posters`.
 * Wizard ids match SessionTypeId (`wizard-<type>`).
 */
export const MAP_POSTER_SHOTS: readonly MapPosterShot[] = [
  {
    id: 'hub-empty',
    ...POSTER_SIZE,
    // Illustrative board on the plain plate — not a live session basemap.
    createSession: () => withPosterPlate(createResidentialGasLeakSession()),
    fit: {
      paddingPx: 40,
      maxZoom: 18.2,
      minSpanM: 100,
      includeIds: ['backhoe-1', 'pipe-1', 'engine-1'],
    },
  },
  {
    id: 'wizard-whiteboard',
    ...POSTER_SIZE,
    createSession: () => withPosterPlate(createResidentialGasLeakSession()),
    fit: {
      paddingPx: 40,
      maxZoom: 18.2,
      minSpanM: 100,
      includeIds: ['backhoe-1', 'pipe-1', 'engine-1'],
    },
  },
  {
    id: 'wizard-walkthrough',
    ...POSTER_SIZE,
    createSession: () => withPosterPlate(createFireEvacuationSession()),
    fit: {
      paddingPx: 44,
      maxZoom: 17.4,
      minSpanM: 160,
      includeIds: ['fire-1', 'school-1', 'evac-1'],
    },
  },
  {
    id: 'wizard-exercise',
    ...POSTER_SIZE,
    createSession: () => withPosterPlate(createYuccaDigInSession()),
    // Fit the dig-in / fire cluster — not outer road closures or dispatch.
    fit: {
      paddingPx: 40,
      maxZoom: 17.2,
      minSpanM: 160,
      excludeTypes: ['label', 'road', 'police', 'wind', 'tornado', 'hurricane', 'icp'],
    },
  },
  /**
   * Plate-only reference (blank map camera). Not wired into UI yet.
   */
  {
    id: 'plate-blank',
    ...POSTER_SIZE,
    createSession: () => withPosterPlate(createBlankBoardSession()),
    // No objects — tighter blank-map neighborhood framing from the seed.
    camera: (session) => ({ ...session.camera, zoom: Math.min(16.4, session.camera.zoom + 1.4) }),
  },
]

export const MAP_POSTER_SHOT_IDS = MAP_POSTER_SHOTS.map((s) => s.id)

export function mapPosterShotById(id: string): MapPosterShot | null {
  return MAP_POSTER_SHOTS.find((s) => s.id === id) ?? null
}

/** Public URL for a committed poster PNG (not under /scenarios — that is the SPA route). */
export function mapPosterUrl(shotId: string): string {
  return `/scenario-posters/${shotId}.png`
}

export function resolvePosterShot(shot: MapPosterShot): {
  session: ScenarioSession
  playheadIso: string
  camera: MapPosterCamera
  width: number
  height: number
} {
  const session = shot.createSession()
  const playheadIso = shot.playheadIso?.(session) ?? defaultPlayhead(session)
  const fitted =
    shot.camera?.(session, playheadIso) ??
    cameraFittingObjects(session, playheadIso, { width: shot.width, height: shot.height }, shot.fit) ??
    cameraAtPlayhead(session, playheadIso)
  return {
    session,
    playheadIso,
    camera: fitted,
    width: shot.width,
    height: shot.height,
  }
}
